import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { 
  PDFGenerationOptions, 
  PDFGenerationResult, 
  PDFData, 
  ResearchPDFData, 
  AppIdeaPDFData, 
  ContentPDFData 
} from './pdf-types';
import { 
  DEFAULT_PDF_OPTIONS, 
  sanitizeHTMLForPDF, 
  generatePDFFilename, 
  waitForImages, 
  calculatePageDimensions, 
  downloadPDF 
} from './pdf-utils';
import { getTemplate } from './pdf-templates';

/**
 * Main PDF generator class
 */
export class PDFGenerator {
  private options: PDFGenerationOptions;

  constructor(options: Partial<PDFGenerationOptions> = {}) {
    this.options = { ...DEFAULT_PDF_OPTIONS, ...options };
  }

  /**
   * Generates PDF from HTML content
   */
  async generateFromHTML(
    html: string, 
    options: Partial<PDFGenerationOptions> = {}
  ): Promise<PDFGenerationResult> {
    try {
      const finalOptions = { ...this.options, ...options };
      const sanitizedHTML = sanitizeHTMLForPDF(html);
      
      // Create temporary container
      const container = this.createTemporaryContainer(sanitizedHTML);
      document.body.appendChild(container);

      try {
        // Wait for images to load
        await waitForImages(container);

        // Generate canvas from HTML
        const canvas = await html2canvas(container, {
          scale: finalOptions.scale || 2,
          useCORS: finalOptions.useCORS || true,
          allowTaint: finalOptions.allowTaint || false,
          backgroundColor: '#ffffff',
          logging: false,
        });

        // Calculate dimensions
        const { width: pageWidth, height: pageHeight } = calculatePageDimensions(
          finalOptions.format || 'a4',
          finalOptions.orientation || 'portrait'
        );

        // Create PDF
        const pdf = new jsPDF({
          orientation: finalOptions.orientation || 'portrait',
          unit: 'mm',
          format: finalOptions.format || 'a4',
        });

        // Calculate image dimensions for PDF
        const imgWidth = pageWidth - (finalOptions.margin?.left || 20) - (finalOptions.margin?.right || 20);
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Add image to PDF with pagination if needed
        await this.addImageToPDF(pdf, canvas, imgWidth, imgHeight, finalOptions);

        // Generate filename
        const filename = finalOptions.filename || generatePDFFilename('document');
        const finalFilename = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;

        // Create blob
        const blob = pdf.output('blob');

        return {
          success: true,
          filename: finalFilename,
          blob,
        };

      } finally {
        // Clean up temporary container
        document.body.removeChild(container);
      }

    } catch (error) {
      console.error('PDF generation failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Generates PDF from research data
   */
  async generateResearchPDF(
    data: ResearchPDFData, 
    options: Partial<PDFGenerationOptions> = {}
  ): Promise<PDFGenerationResult> {
    const template = getTemplate('research');
    const html = template.template(data);
    const finalOptions = { ...template.defaultOptions, ...options };
    
    if (!finalOptions.filename) {
      finalOptions.filename = generatePDFFilename(data.title, 'research');
    }

    return this.generateFromHTML(html, finalOptions);
  }

  /**
   * Generates PDF from app idea data
   */
  async generateAppIdeaPDF(
    data: AppIdeaPDFData, 
    options: Partial<PDFGenerationOptions> = {}
  ): Promise<PDFGenerationResult> {
    const template = getTemplate('appIdea');
    const html = template.template(data);
    const finalOptions = { ...template.defaultOptions, ...options };
    
    if (!finalOptions.filename) {
      finalOptions.filename = generatePDFFilename(data.title, 'app-idea');
    }

    return this.generateFromHTML(html, finalOptions);
  }

  /**
   * Generates PDF from general content data
   */
  async generateContentPDF(
    data: ContentPDFData, 
    options: Partial<PDFGenerationOptions> = {}
  ): Promise<PDFGenerationResult> {
    const template = getTemplate('content');
    const html = template.template(data);
    const finalOptions = { ...template.defaultOptions, ...options };
    
    if (!finalOptions.filename) {
      finalOptions.filename = generatePDFFilename(data.title, 'content');
    }

    return this.generateFromHTML(html, finalOptions);
  }

  /**
   * Auto-detects data type and generates appropriate PDF
   */
  async generatePDF(
    data: PDFData, 
    options: Partial<PDFGenerationOptions> = {}
  ): Promise<PDFGenerationResult> {
    if (this.isResearchData(data)) {
      return this.generateResearchPDF(data, options);
    } else if (this.isAppIdeaData(data)) {
      return this.generateAppIdeaPDF(data, options);
    } else {
      return this.generateContentPDF(data, options);
    }
  }

  /**
   * Generates and downloads PDF
   */
  async generateAndDownload(
    data: PDFData, 
    options: Partial<PDFGenerationOptions> = {}
  ): Promise<PDFGenerationResult> {
    const result = await this.generatePDF(data, options);
    
    if (result.success && result.blob && result.filename) {
      downloadPDF(result.blob, result.filename);
    }
    
    return result;
  }

  /**
   * Creates a temporary container for HTML rendering
   */
  private createTemporaryContainer(html: string): HTMLElement {
    const container = document.createElement('div');
    container.innerHTML = html;
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.width = '210mm'; // A4 width
    container.style.backgroundColor = '#ffffff';
    container.style.fontFamily = 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif';
    return container;
  }

  /**
   * Adds image to PDF with pagination support
   */
  private async addImageToPDF(
    pdf: jsPDF, 
    canvas: HTMLCanvasElement, 
    imgWidth: number, 
    imgHeight: number, 
    options: PDFGenerationOptions
  ): Promise<void> {
    const pageHeight = calculatePageDimensions(
      options.format || 'a4',
      options.orientation || 'portrait'
    ).height;
    
    const marginTop = options.margin?.top || 20;
    const marginBottom = options.margin?.bottom || 20;
    const marginLeft = options.margin?.left || 20;
    
    const maxHeight = pageHeight - marginTop - marginBottom;
    
    if (imgHeight <= maxHeight) {
      // Single page
      const imgData = canvas.toDataURL('image/jpeg', options.quality || 0.95);
      pdf.addImage(imgData, 'JPEG', marginLeft, marginTop, imgWidth, imgHeight);
    } else {
      // Multiple pages
      const totalPages = Math.ceil(imgHeight / maxHeight);
      
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }
        
        // Calculate the portion of the image for this page
        const sourceY = page * maxHeight * (canvas.height / imgHeight);
        const sourceHeight = Math.min(maxHeight * (canvas.height / imgHeight), canvas.height - sourceY);
        
        // Create a canvas for this page portion
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sourceHeight;
        
        const pageCtx = pageCanvas.getContext('2d');
        if (pageCtx) {
          pageCtx.drawImage(
            canvas,
            0, sourceY, canvas.width, sourceHeight,
            0, 0, canvas.width, sourceHeight
          );
          
          const pageImgData = pageCanvas.toDataURL('image/jpeg', options.quality || 0.95);
          const pageImgHeight = (sourceHeight * imgWidth) / canvas.width;
          
          pdf.addImage(pageImgData, 'JPEG', marginLeft, marginTop, imgWidth, pageImgHeight);
        }
      }
    }
  }

  /**
   * Type guards for data detection
   */
  private isResearchData(data: PDFData): data is ResearchPDFData {
    return 'researchType' in data && 'content' in data;
  }

  private isAppIdeaData(data: PDFData): data is AppIdeaPDFData {
    return 'category' in data && 'description' in data && !('researchType' in data);
  }
}

/**
 * Convenience functions for direct use
 */

/**
 * Creates a new PDF generator instance
 */
export const createPDFGenerator = (options?: Partial<PDFGenerationOptions>): PDFGenerator => {
  return new PDFGenerator(options);
};

/**
 * Quick function to generate and download a PDF
 */
export const generateAndDownloadPDF = async (
  data: PDFData, 
  options?: Partial<PDFGenerationOptions>
): Promise<PDFGenerationResult> => {
  const generator = createPDFGenerator(options);
  return generator.generateAndDownload(data, options);
};

/**
 * Quick function to generate a PDF blob
 */
export const generatePDFBlob = async (
  data: PDFData, 
  options?: Partial<PDFGenerationOptions>
): Promise<PDFGenerationResult> => {
  const generator = createPDFGenerator(options);
  return generator.generatePDF(data, options);
};

/**
 * Generate PDF from HTML string
 */
export const generatePDFFromHTML = async (
  html: string, 
  options?: Partial<PDFGenerationOptions>
): Promise<PDFGenerationResult> => {
  const generator = createPDFGenerator(options);
  return generator.generateFromHTML(html, options);
}; 